import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadsManager({ token }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers });
      setLeads(data || []);
    } catch {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  return (
    <div data-testid="leads-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl">Quote Requests</h2>
          <p className="text-sm text-white/60 mt-1">All leads submitted via the website quote form.</p>
        </div>
        <div className="text-sm text-white/60" data-testid="leads-count">
          {loading ? <Loader2 size={14} className="animate-spin inline mr-2" /> : null}
          {leads.length} total
        </div>
      </div>

      {leads.length === 0 && !loading && (
        <div className="text-center py-16 text-white/50" data-testid="leads-empty">
          No leads yet — they'll appear here as soon as someone submits a quote.
        </div>
      )}

      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.id} className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid={`lead-${l.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <div className="font-display text-xl">{l.name}</div>
                  <span className="px-2.5 py-0.5 rounded-full bg-lime text-dark text-xs font-semibold">{l.service}</span>
                  {l.location && <span className="text-xs text-white/60">{l.location}</span>}
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-white/75">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-lime"><Mail size={13}/> {l.email}</a>
                  <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 hover:text-lime"><Phone size={13}/> {l.phone}</a>
                  {l.postcode && <span className="flex items-center gap-1.5"><MapPin size={13}/> {l.postcode}</span>}
                </div>
                {l.message && (
                  <p className="text-sm text-white/70 mt-3 whitespace-pre-wrap line-clamp-4">{l.message}</p>
                )}
              </div>
              <div className="text-right text-xs text-white/40 shrink-0">
                <div>{new Date(l.created_at).toLocaleString("en-GB")}</div>
                <div className="mt-1">via {l.source_page}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
